import { getReportArchiveUrl } from '../resident-report/report-api';
import { getReportListHref } from './get-report-id-from-url';
import type { ReportEditPayload } from '../resident-report-drawer/report-edit-payload';

export interface ReportDetailMetricRow {
  label: string;
  plan: string;
  fact: string;
  deviation: string;
}

export interface ReportDetailDocument {
  name: string;
  url: string;
  format: string;
  size: string;
}

export interface ReportDetailViewModel {
  id: number;
  title: string;
  status: string;
  statusModifier: string;
  isSigned: boolean;
  createdAt: string;
  companyName: string;
  reportYear: string;
  commentReview?: string;
  managerFiles: ReportDetailDocument[];
  backUrl: string;
  downloadUrl: string;
  editUrl: string;
  metrics: ReportDetailMetricRow[];
  details: Array<{ label: string; value: string }>;
  documents: ReportDetailDocument[];
  editPayload: ReportEditPayload;
}

interface ApiReportMetrics {
  revenue?: { plan?: string; fact?: string };
  invest_infra?: { plan?: string; fact?: string };
  invest_rnd?: { plan?: string; fact?: string };
  employees?: { plan?: string; fact?: string };
  export_share?: { plan?: string; fact?: string };
  rid?: { plan?: string; fact?: string };
}

export interface ApiReportData {
  companyName?: string;
  reportYear?: string;
  metrics?: ApiReportMetrics;
  revenue_dev?: string;
  invest_infra_dev?: string;
  invest_rnd_dev?: string;
  employees_dev?: string;
  export_share_dev?: string;
  rid_dev?: string;
  comment?: string;
  commentReview?: string | null;
  managerComment?: string;
  commentManager?: string;
  revisionComment?: string;
  rejectComment?: string;
  comment_manager?: string;
  projectNames?: string;
  projectReadiness?: string;
  rids?: string;
  commercialization?: string;
  stateSupport?: string;
  attachments?: Array<{ id?: string | number; name?: string; url?: string; size?: string }>;
  files?: Array<
    | string
    | { id?: string | number; name?: string; url?: string; size?: string; fileName?: string }
  >;
  status?: string | null;
  isSigned?: boolean;
  dateSign?: string | null;
  fioSignatory?: string | null;
  nameOrganization?: string | null;
  certAuthority?: string | null;
  certNumber?: string | null;
  certValidUntil?: string | null;
  verifyResult?: boolean | null;
  createdAt?: string | null;
}

const METRIC_ROWS: Array<{
  key: keyof ApiReportMetrics;
  devKey: keyof ApiReportData;
  label: string;
}> = [
  { key: 'revenue', devKey: 'revenue_dev', label: 'Выручка, руб' },
  {
    key: 'invest_infra',
    devKey: 'invest_infra_dev',
    label: 'Инвестиции в инфраструктуру ИНТЦ МГУ, руб',
  },
  { key: 'invest_rnd', devKey: 'invest_rnd_dev', label: 'Инвестиции в НИОКР, руб' },
  { key: 'export_share', devKey: 'export_share_dev', label: 'Доля экспорта, %' },
  {
    key: 'employees',
    devKey: 'employees_dev',
    label: 'Численность работников организации, чел.',
  },
  {
    key: 'rid',
    devKey: 'rid_dev',
    label: 'Кол-во результатов интеллектуальной деятельности, шт',
  },
];

const DEFAULT_STATUS = 'Черновик';

function displayValue(value?: string | null): string {
  return value?.trim() ? value : '-';
}

function formatReportYear(year?: string | null): string {
  return year ? `${year} г.` : '-';
}

export function getStatusModifier(status?: string | null): string {
  const normalized = (status || DEFAULT_STATUS).toLowerCase();

  if (normalized.includes('принят')) return 'accepted';
  if (normalized.includes('доработ')) return 'revision';
  if (normalized.includes('рассмотр')) return 'review';

  return 'draft';
}

export type ReportStatusModifier = 'draft' | 'review' | 'accepted' | 'revision';

export interface ReportDetailActions {
  showSignature: boolean;
  canDownload: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export function getReportDetailActions(statusModifier: string): ReportDetailActions {
  switch (statusModifier as ReportStatusModifier) {
    case 'review':
      return {
        showSignature: true,
        canDownload: true,
        canEdit: true,
        canDelete: false,
      };
    case 'accepted':
      return {
        showSignature: true,
        canDownload: true,
        canEdit: false,
        canDelete: false,
      };
    case 'revision':
      return {
        showSignature: true,
        canDownload: true,
        canEdit: true,
        canDelete: false,
      };
    case 'draft':
    default:
      return {
        showSignature: false,
        canDownload: true,
        canEdit: true,
        canDelete: true,
      };
  }
}

function formatDate(iso?: string | null): string {
  if (!iso) return '-';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  return date.toLocaleDateString('ru-RU');
}

function getDocumentFormat(name?: string): string {
  const extension = name?.split('.').pop()?.toUpperCase();
  return extension || 'FILE';
}

function resolveFileUrl(url: string): string {
  const normalized = url.trim();
  if (!normalized) return '#';
  if (/^https?:\/\//i.test(normalized)) return normalized;
  if (normalized.startsWith('/')) return normalized;
  return `/${normalized}`;
}

function normalizeReviewFiles(
  files?: ApiReportData['files'],
): ReportDetailDocument[] {
  if (!files?.length) {
    return [];
  }

  return files
    .map((file): ReportDetailDocument | null => {
      if (typeof file === 'string') {
        const url = resolveFileUrl(file);
        const name = decodeURIComponent(url.split('/').pop() || 'Файл');

        return {
          name,
          url,
          format: getDocumentFormat(name),
          size: '-',
        };
      }

      if (!file || typeof file !== 'object') {
        return null;
      }

      const name = String(file.name ?? file.fileName ?? 'Файл').trim() || 'Файл';
      const url = resolveFileUrl(String(file.url ?? ''));

      return {
        name,
        url: url === '/' ? '#' : url,
        format: getDocumentFormat(name),
        size: file.size?.trim() ? String(file.size) : '-',
      };
    })
    .filter((file): file is ReportDetailDocument => file !== null);
}

export function mapApiReportToEditPayload(id: number, data: ApiReportData): ReportEditPayload {
  return {
    id,
    status: data.status ?? null,
    companyName: data.companyName || '',
    reportYear: data.reportYear || '',
    comment: data.comment,
    projectNames: data.projectNames,
    projectReadiness: data.projectReadiness,
    rids: data.rids,
    commercialization: data.commercialization,
    stateSupport: data.stateSupport,
    metrics: METRIC_ROWS.map(({ key }) => ({
      key,
      plan: parseNumber(data.metrics?.[key]?.plan),
      fact: parseNumber(data.metrics?.[key]?.fact),
    })),
    documents: (data.attachments || []).map((file) => ({
      name: file.name || 'Файл',
      url: file.url || '#',
      size: parseSizeToBytes(file.size),
    })),
  };
}

function parseNumber(value?: string | null): number | null {
  if (!value?.trim()) return null;
  const parsed = Number(value.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

function parseSizeToBytes(size?: string): number | undefined {
  if (!size) return undefined;

  const match = size.match(/([\d.,]+)\s*(MB|KB|GB)?/i);
  if (!match) return undefined;

  const amount = Number(match[1].replace(',', '.'));
  if (!Number.isFinite(amount)) return undefined;

  const unit = (match[2] || 'MB').toUpperCase();
  if (unit === 'KB') return amount * 1024;
  if (unit === 'GB') return amount * 1024 * 1024 * 1024;
  return amount * 1024 * 1024;
}

export function mapApiReportToDetailView(id: number, data: ApiReportData): ReportDetailViewModel {
  const companyName = data.companyName || '-';
  const reportYear = data.reportYear || '';
  const status = data.status || DEFAULT_STATUS;
  const commentReview = data.commentReview?.trim() || undefined;

  return {
    id,
    title: `Отчет. ${companyName}. ${formatReportYear(reportYear)}`,
    status,
    statusModifier: getStatusModifier(status),
    isSigned: Boolean(data.isSigned),
    createdAt: formatDate(data.createdAt ?? data.dateSign),
    companyName,
    reportYear: formatReportYear(reportYear),
    commentReview,
    managerFiles: normalizeReviewFiles(data.files),
    backUrl: getReportListHref(),
    downloadUrl: getReportArchiveUrl(id),
    editUrl: `/lk/resident/reports/?Id=${id}`,
    metrics: METRIC_ROWS.map(({ key, devKey, label }) => ({
      label,
      plan: displayValue(data.metrics?.[key]?.plan),
      fact: displayValue(data.metrics?.[key]?.fact),
      deviation: displayValue(String(data[devKey] ?? '')),
    })),
    details: [
      { label: 'Общий комментарий', value: displayValue(data.comment) },
      { label: 'Наименование проекта', value: displayValue(data.projectNames) },
      {
        label: 'Стадия готовности реализованного НТП, по ГОСТ-Р 71726-2024',
        value: displayValue(data.projectReadiness),
      },
      { label: 'Перечень РИД, возникших при реализации НТП', value: displayValue(data.rids) },
      {
        label: 'Сведения о вовлечении результатов НТД в гражданский оборот',
        value: displayValue(data.commercialization),
      },
      {
        label: 'Сведения об использовании государственных мер поддержки',
        value: displayValue(data.stateSupport),
      },
    ],
    documents: (data.attachments || []).map((file) => ({
      name: file.name || 'Файл',
      url: file.url || '#',
      format: getDocumentFormat(file.name),
      size: file.size || '-',
    })),
    editPayload: mapApiReportToEditPayload(id, data),
  };
}
